import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, PasswordInput } from "@/components/Layout";
import api, { errorText } from "@/lib/api";
import Select from "@/components/Select";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";

const CONFIRM_WORD = "anonimizează";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO") : "—");

// Parametrii > GDPR. Platforma NU are valori implicite: termenele de păstrare, lista câmpurilor anonimizate și formatul
// exportului le completează clientul, prin responsabilul lui GDPR. Gol = platforma nu face nimic din acea categorie.
export default function GdprSettings() {
  const { has } = useAuth();
  const canEdit = has("gdpr.settings_manage");
  const [cat, setCat] = useState(null);                // catalog: categorii, câmpuri, formate
  const [form, setForm] = useState(null);              // { retention: {cheie: "text"}, fields: Set, format }
  const [eligible, setEligible] = useState({ items: [], note: null });
  const [anon, setAnon] = useState(null);              // { person, confirm, pw }
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const flash = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3500); };

  const loadEligible = () => api.get("/gdpr/eligible", { params: { limit: 500 } }).then(r => setEligible(r.data)).catch(() => {});
  const load = async () => {
    try {
      const r = await api.get("/gdpr/settings");
      setCat(r.data.catalog);
      const s = r.data.settings;
      setForm({ retention: Object.fromEntries(Object.entries(s.retention_years).map(([k, v]) => [k, v == null ? "" : String(v)])),
                fields: new Set(s.anonymized_fields), format: s.export_format || "" });
    } catch (e) { setMsg({ text: errorText(e, "Setările GDPR nu s-au putut încărca"), type: "error" }); }
    loadEligible();
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const retention_years = {};
    for (const [k, v] of Object.entries(form.retention)) {
      const t = String(v).trim();
      if (t === "") { retention_years[k] = null; continue; }
      if (!/^\d+$/.test(t)) return flash("Termenele sunt numere întregi de ani (sau goale).", "error");
      retention_years[k] = parseInt(t, 10);
    }
    try {
      await api.put("/gdpr/settings", { retention_years, anonymized_fields: [...form.fields], export_format: form.format || null });
      flash("Setările GDPR au fost salvate."); loadEligible();
    } catch (e) { flash(errorText(e, "Eroare"), "error"); }
  };

  const toggle = (key) => setForm(f => { const s = new Set(f.fields); s.has(key) ? s.delete(key) : s.add(key); return { ...f, fields: s }; });

  const doAnonymize = async () => {
    try {
      const r = await api.post(`/gdpr/persons/${anon.person.person_id}/anonymize`, { confirm: anon.confirm, actor_password: anon.pw });
      setAnon(null); loadEligible();
      flash(`Persoana a fost anonimizată (${r.data.documents_deleted} documente șterse din dosar).`);
    } catch (e) { flash(errorText(e, "Eroare"), "error"); }
  };

  if (!form || !cat) return (<><PageHeader title="GDPR" /><Toast msg={msg.text} type={msg.type} /></>);

  return (
    <>
      <PageHeader title="GDPR" subtitle="Termene de păstrare, anonimizare și export al datelor unei persoane"
        actions={<button className="btn btn-primary" onClick={save} data-testid="gdpr-save" {...lock(canEdit)}>Salvează setările</button>} />
      <Toast msg={msg.text} type={msg.type} />

      <div style={{ background: "#FEF3C7", color: "#78350F", padding: "10px 12px", borderRadius: 6, fontSize: 13, marginBottom: 14 }}>
        Platforma <b>nu are valori implicite</b>: ce completați aici stabilește responsabilul GDPR al organizației dumneavoastră.
        Ce rămâne gol înseamnă „nu fac nimic": nu se propune nicio anonimizare și exportul nu pornește.
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginTop: 0 }}>Termene de păstrare (ani)</h3>
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>
          Termenul „dosar personal" se numără de la data încetării ultimului raport de muncă al persoanei.
        </div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          {cat.categories.map(c => (
            <div key={c.key}>
              <label className="label">{c.label}</label>
              <input className="input" inputMode="numeric" placeholder="necompletat" value={form.retention[c.key]} disabled={!canEdit}
                data-testid={`gdpr-term-${c.key}`} onChange={e => setForm({ ...form, retention: { ...form.retention, [c.key]: e.target.value } })} />
              <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                {c.applied ? "Se aplică la anonimizare." : "Se salvează; nu se aplică încă automat."}
              </div>
            </div>))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginTop: 0 }}>Câmpuri anonimizate</h3>
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>
          Câmpurile bifate devin neidentificabile la anonimizare. Contul de acces se închide mereu, iar documentele din dosar se șterg.
          Rapoartele de muncă rămân, fără identitate, pentru statistici.
        </div>
        {cat.field_groups.map(g => (
          <div key={g.group} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{g.group}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
              {g.fields.map(f => (
                <label key={f.key} style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}>
                  <input type="checkbox" checked={form.fields.has(f.key)} disabled={!canEdit} onChange={() => toggle(f.key)} data-testid={`gdpr-field-${f.key}`} />
                  {f.label}
                </label>))}
            </div>
          </div>))}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginTop: 0 }}>Formatul exportului datelor unei persoane</h3>
        <div style={{ maxWidth: 480 }}>
          <Select className="select" value={form.format} disabled={!canEdit} onChange={e => setForm({ ...form, format: e.target.value })} data-testid="gdpr-format">
            <option value="">— necompletat (exportul nu pornește) —</option>
            {cat.export_formats.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </Select>
        </div>
        <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 6 }}>
          Exportul se face din lista „Angajați" (acțiunea „Export GDPR") și cere parola dumneavoastră.
        </div>
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        <h3 style={{ marginTop: 0 }}>Persoane eligibile pentru anonimizare</h3>
        {eligible.note && <div style={{ fontSize: 13, color: "#475569" }} data-testid="gdpr-note">{eligible.note}</div>}
        {!eligible.note && (
          <table className="tbl" data-testid="gdpr-eligible">
            <thead><tr><th>Persoană</th><th>Marcă</th><th>Ultimul raport încetat</th><th>Termen expirat la</th><th></th></tr></thead>
            <tbody>
              {eligible.items.map(p => (
                <tr key={p.person_id}>
                  <td style={{ fontWeight: 600 }}>{p.name}</td><td>{p.marca}</td><td>{fmtDate(p.last_end_date)}</td><td>{fmtDate(p.eligible_from)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn btn-danger" data-testid={`gdpr-anon-${p.person_id}`} {...lock(has("gdpr.anonymize"))}
                      onClick={() => setAnon({ person: p, confirm: "", pw: "" })}>Anonimizează…</button>
                  </td>
                </tr>))}
              {eligible.items.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "#94A3B8", padding: 20 }}>Nicio persoană nu are termenul expirat.</td></tr>}
            </tbody>
          </table>)}
        <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 8 }}>
          Platforma doar propune; nimic nu se anonimizează singur. Anonimizarea se face persoană cu persoană, după confirmare.
        </div>
      </div>

      {anon && (
        <Modal title={`Anonimizează — ${anon.person.name}`} onClose={() => setAnon(null)} width={520}>
          <div style={{ background: "#FEE2E2", color: "#7F1D1D", padding: "10px 12px", borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            <b>Acțiunea este ireversibilă.</b> Câmpurile alese devin neidentificabile, contul de acces se închide definitiv și documentele din
            dosarul electronic se șterg. O copie de siguranță mai veche ar readuce datele.
          </div>
          <label className="label label-req">Scrieți cuvântul „{CONFIRM_WORD}" pentru confirmare</label>
          <input className="input" value={anon.confirm} onChange={e => setAnon({ ...anon, confirm: e.target.value })} data-testid="gdpr-confirm" autoComplete="off" />
          <label className="label label-req" style={{ marginTop: 10 }}>Parola dumneavoastră</label>
          <PasswordInput value={anon.pw} onChange={e => setAnon({ ...anon, pw: e.target.value })} testId="gdpr-actor-pw" autoComplete="current-password" name="onomad-actor-password" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setAnon(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={doAnonymize} data-testid="gdpr-anon-confirm">Anonimizează definitiv</button>
          </div>
        </Modal>)}
    </>
  );
}
